import { FC, Fragment, useMemo, useState, useCallback } from 'react';
import { Box, Button, Chip, Collapse, Divider, IconButton, MenuItem, Paper, styled, Tooltip, Checkbox as MuiCheckbox, FormControlLabel } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faChevronDown, faChevronUp, faCodeBranch, faInputText, faObjectGroup, faSquareList, faTableList } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, InputOnlyField, Text, Markdown, IValidationError, Select, Multiselect } from "@ngt/forms";
import ConfigurationFormEditPanel, { ConfigurationBox } from './ConfigurationFormEditPanel';
import { ConfigurationType, IConfigurationField, IConfigurationSection, IConfigurationVersion } from "@/utilities/forms/ConfigurationFormProcessor";
import { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import { useScopedField, useFormActions, Condition, ConditionMode, IConditionProps, IFormState } from '@ngt/forms-core';
import { FieldType, IFormFieldMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceDataConfigurationForm, ISourceDataConfigurationFormField, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormVersion, NoteStatus, NoteType, SourceDataPriority } from "@/api/dtos";
import { ConfigurationTitle } from './ConfigurationFormEditPanel';
import { IEventMetadata } from '../../../../api/dtos';
import ConfigurationFormEditInfo, { ConfigurationDividerBox, ConfigurationFlexBox, OtherVersionsBox, OtherVersionsItem } from "./ConfigurationFormEditInfo";
import ConfigurationFormEditUpdate from "./ConfigurationFormEditUpdate";
import ConfigurationFormEditCopy from "./ConfigurationFormEditCopy";
import { get } from 'lodash-es'
import { faExclamationTriangle, faTriangle } from '@fortawesome/pro-solid-svg-icons';

export interface IConfigurationFormEditFieldProps {
    context: IConfigurationFormEditContext<IConfigurationField>
}

interface IOtherVersion {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    formKey: string;
    formMetadata: IFormMetadata;
    formVersionKey: string;
    formVersionMetadata: IFormVersionMetadata;
    formSectionKey?: string;
    formSectionMetadata?: IFormSectionMetadata;
    formFieldKey: string;
    formFieldMetadata: IFormFieldMetadata;
    formFieldTypeLabel: string;
    formFieldConfiguration: ISourceDataConfigurationFormField;
    configurationPropertyPath: string;
    linked: boolean;
    isGlobal: boolean;
    isTrial: boolean;
}


const onConditionNotMet = () => {};

const ConfigurationFormEditField: FC<IConfigurationFormEditFieldProps> = ({
    context
}) => {
    const {
        configurationPropertyPath
    } = context;
    
    const [openOtherVersions, setOpenOtherVersions] = useState(false);
    
    const actions = useFormActions();

    const allVersions = useMemo(() => {
        if (!context.item.formMetadata?.versions || context.usingTrialConfiguration) {
            return null;
        }

        let currentVersion: IOtherVersion | undefined = undefined;

        const versionKeys = context.item.formMetadata.versions.map(x => x.id);

        const versions: IOtherVersion[] = [];

        const sectionPartialPath = context.item.formSectionKey ? `.sections.${context.item.formSectionKey}` : '';

        let fieldTypeLabel = 'Unknown';

        switch (context.item.formFieldMetadata?.fieldType) {
            case FieldType.Checkbox:
                fieldTypeLabel = 'Checkbox';
                break;
            case FieldType.Ctcae:
                fieldTypeLabel = 'CTCAE';
                break;
            case FieldType.Date:
                fieldTypeLabel = 'Date';
                break;
            case FieldType.File:
                fieldTypeLabel = 'File';
                break;
            case FieldType.MultiSelect:
                fieldTypeLabel = 'MultiSelect';
                break;
            case FieldType.MultilineText:
                fieldTypeLabel = 'MultilineText';
                break;
            case FieldType.Numeric:
                fieldTypeLabel = 'Numeric';
                break;
            case FieldType.Radio:
                fieldTypeLabel = 'Radio';
                break;
            case FieldType.Select:
                fieldTypeLabel = 'Select';
                break;
            case FieldType.Text:
                fieldTypeLabel = 'Text';
                break;
            default:
                fieldTypeLabel = 'Unknown';
                break;
        }

        for (let versionKey of versionKeys) {
            const versionMetadata = context.item.formMetadata?.versions?.find(x => x.id === versionKey);
            const sectionMetadata = versionMetadata?.sections?.find(x => x.id === context.item.formSectionKey);
            const fieldMetadata = versionMetadata?.fields?.find(x => x.id === context.item.formFieldKey);

            if (fieldMetadata?.fieldType !== context.item.formFieldMetadata?.fieldType) {
                continue;
            }

            if (context.item.formEvents) {
                for (let event of context.item.formEvents[context.item.formKey!]) {
                    const localPropertyPath = `configuration.events.${event.eventKey}.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}.fields.${context.item.formFieldKey}`
                    const localFormVersion = actions.getFieldValue<ISourceDataConfigurationFormField>(localPropertyPath);

                    if (localFormVersion) {
                        const data = {
                            type: ConfigurationType.Field,
                            eventKey: event.eventKey,
                            eventMetadata: event.eventMetadata,
                            formKey: context.item.formKey!,
                            formMetadata: context.item.formMetadata!,
                            formVersionKey: versionKey!,
                            formVersionMetadata: versionMetadata!,
                            formSectionKey: context.item.formSectionKey,
                            formSectionMetadata: sectionMetadata,
                            formFieldKey: context.item.formFieldKey!,
                            formFieldMetadata: fieldMetadata!,
                            formFieldTypeLabel: fieldTypeLabel,
                            formFieldConfiguration: localFormVersion,
                            configurationPropertyPath: localPropertyPath,
                            linked: !!localFormVersion?.linked,
                            isGlobal: false,
                            isTrial: !!context.item.isTrialConfiguration
                        }

                        if (context.configurationPropertyPath !== localPropertyPath) {
                            versions.push(data)
                        }
                        else {
                            currentVersion = data;
                        }
                    }
                }
            }

            const globalPropertyPath = `configuration.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}.fields.${context.item.formFieldKey}`;
            const globalFormVersion = actions.getFieldValue<ISourceDataConfigurationFormField>(globalPropertyPath);
            
            if (globalFormVersion) {
                const data = {
                    type: ConfigurationType.Field,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formSectionKey: context.item.formSectionKey,
                    formSectionMetadata: sectionMetadata,
                    formFieldKey: context.item.formFieldKey!,
                    formFieldMetadata: fieldMetadata!,
                    formFieldTypeLabel: fieldTypeLabel,
                    formFieldConfiguration: globalFormVersion,
                    configurationPropertyPath: globalPropertyPath,
                    linked: !!globalFormVersion?.linked,
                    isGlobal: !context.item.isTrialConfiguration,
                    isTrial: !!context.item.isTrialConfiguration
                }

                if (context.configurationPropertyPath !== globalPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }

            const trialPropertyPath = `trialConfiguration.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}.fields.${context.item.formFieldKey}`
            const trialFormVersion = actions.getFieldValue<ISourceDataConfigurationFormField>(trialPropertyPath);
            
            if (trialFormVersion) {
                const data = {
                    type: ConfigurationType.Field,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formSectionKey: context.item.formSectionKey,
                    formSectionMetadata: sectionMetadata,
                    formFieldKey: context.item.formFieldKey!,
                    formFieldMetadata: fieldMetadata!,
                    formFieldTypeLabel: fieldTypeLabel,
                    formFieldConfiguration: trialFormVersion,
                    configurationPropertyPath: trialPropertyPath,
                    linked: !!trialFormVersion?.linked,
                    isGlobal: false,
                    isTrial: true
                }

                if (context.configurationPropertyPath !== trialPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }


        }

        if (versions.length === 0) {
            return null;
        }

        return {
            otherVersions: versions.sort((a, b) => {

                if (a.eventKey !== b.eventKey) {
                    return (a.eventKey ?? 'zzz') < (b.eventKey ?? 'zzz') ? -1 : 1;
                }
                
                if (a.formKey !== b.formKey) {
                    return a.formKey < b.formKey ? -1 : 1;
                }
                
                if (a.formVersionKey !== b.formVersionKey) {
                    return a.formVersionKey < b.formVersionKey ? -1 : 1;
                }
                
                if (a.formSectionKey !== b.formSectionKey) {
                    return (a.formSectionKey ?? 'zzz') < (b.formSectionKey ?? 'zzz') ? -1 : 1;
                }
                
                if (a.formFieldKey !== b.formFieldKey) {
                    return a.formFieldKey < b.formFieldKey ? -1 : 1;
                }
                
                return -1;
            }),
            currentVersion
        };
    }, [context.item.formMetadata?.versions, context.item, context.usingTrialConfiguration, context.usingGlobalConfiguration, actions])

    const otherVersionContent = useMemo(() => {

        if (!allVersions || !allVersions.otherVersions) {
            return null;
        }

        return (
            <OtherVersionsBox>
                {
                    allVersions.otherVersions.map((x, i, a) => {            
                        return (
                            <OtherVersionsItem key={x.configurationPropertyPath}>
                                <ConfigurationFormEditInfo 
                                    {...x}
                                    dividers
                                />
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                                <ConfigurationFlexBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {
                                        !!allVersions.currentVersion && (
                                            <>
                                                <ConfigurationFormEditCopy 
                                                    target={allVersions.currentVersion}
                                                    source={x}
                                                />
                                                <ConfigurationFormEditUpdate 
                                                    target={x}
                                                    source={allVersions.currentVersion}
                                                />
                                            </>
                                        )
                                    }
                                </ConfigurationFlexBox>
                            </OtherVersionsItem>
                        )
                    })
                }
            </OtherVersionsBox>
        )
    }, [allVersions, context.item]);

    const showNoteCondition = useCallback((state: IFormState<any, IValidationError>, parentPropertyPath?: string | null) => {
        const showNotes = get(state.value, `${configurationPropertyPath}.showNotes`);

        return !!showNotes;
    }, [configurationPropertyPath])

    const allowSourceDataVerificationCondition = useCallback((state: IFormState<any, IValidationError>, parentPropertyPath?: string | null) => {
        const allow = get(state.value, `${configurationPropertyPath}.allowSourceDataVerification`);

        return !!allow;
    }, [configurationPropertyPath]);

    const allowFindingsCondition =  useCallback((state: IFormState<any, IValidationError>, parentPropertyPath?: string | null) => {
        const allow = get(state.value, `${configurationPropertyPath}.allowFindings`);

        return !!allow;
    }, [configurationPropertyPath]);

    return (
        <ConfigurationFormEditPanel 
            context={context} 
            type="Field"
            typeIcon={faInputText}
        >
            {
                allVersions?.otherVersions && (
                    <>
                        <ConfigurationTitle 
                            variant="h2" 
                            {...{component: Button} as any} 
                            color='inherit' 
                            sx={{ 
                                display: 'flex', 
                                padding: 0,
                                textTransform: 'none'
                            }}
                            onClick={() => setOpenOtherVersions(c => !c)}
                        >
                            Other Versions
                            <Chip 
                                label={allVersions.otherVersions.length}
                                color="primary"
                                sx={{
                                    ml: 'auto'
                                }}
                                size="small"
                            />
                            <IconButton component="div">
                                <FontAwesomeIcon icon={openOtherVersions ? faChevronUp : faChevronDown} />
                            </IconButton>
                        </ConfigurationTitle>
                        {
                            openOtherVersions && (
                                otherVersionContent
                            )
                        }
                    </>
                )
            }
            <ConfigurationTitle variant="h2">
                Options
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Override Name"
                    propertyPath={`${configurationPropertyPath}.name`}
                    component={Text}
                    margin="normal"
                />
                <InputOnlyField 
                    label="Priority"
                    propertyPath={`${configurationPropertyPath}.priority`}
                    component={Select}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                >
                    <MenuItem
                        value={SourceDataPriority.Low}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Low 
                            <Box sx={{ml: 'auto', color: 'success.main', transform: 'rotate(180deg)'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Normal}
                    >
                        Normal
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.High}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            High
                            <Box sx={{ml: 'auto', color: 'warning.main'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Critical}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Critical
                            <Box sx={{ml: 'auto', color: 'error.main'}}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                </InputOnlyField>
                <InputOnlyField 
                    label="Hide Field When No Data Provided"
                    propertyPath={`${configurationPropertyPath}.excludeBlank`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
            </ConfigurationBox>
            


            <ConfigurationTitle variant="h4">
                Source Data Verification
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Allow Source Data Verification"
                    propertyPath={`${configurationPropertyPath}.allowSourceDataVerification`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
                <Condition 
                    mode={context.usingTrialConfiguration ? ConditionMode.Enable : ConditionMode.Show}
                    subscription={{ value: true }}
                    condition={allowSourceDataVerificationCondition}
                    onConditionNotMet={onConditionNotMet}
                >
                    <InputOnlyField 
                        label="Allow Review"
                        propertyPath={`${configurationPropertyPath}.allowReview`}
                        component={Checkbox}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    />
                    <InputOnlyField 
                        label="Allow Verification"
                        propertyPath={`${configurationPropertyPath}.allowVerify`}
                        component={Checkbox}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    />
                </Condition>
            </ConfigurationBox>

            <ConfigurationTitle variant="h4">
                Findings
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Allow Findings"
                    propertyPath={`${configurationPropertyPath}.allowFindings`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
            </ConfigurationBox>

            <ConfigurationTitle variant="h4">
                Alerts
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Information"
                    propertyPath={`${configurationPropertyPath}.alertInformation`}
                    component={Markdown}
                    margin="normal"
                    //color="info"
                />
                <InputOnlyField 
                    label="Warning"
                    propertyPath={`${configurationPropertyPath}.alertWarning`}
                    component={Markdown}
                    margin="normal"
                    //color="warning"
                />
                <InputOnlyField 
                    label="Error"
                    propertyPath={`${configurationPropertyPath}.alertError`}
                    component={Markdown}
                    margin="normal"
                    //color="error"
                />
                <InputOnlyField 
                    label="Success"
                    propertyPath={`${configurationPropertyPath}.alertSuccess`}
                    component={Markdown}
                    margin="normal"
                    //color="success"
                />
            </ConfigurationBox>

            <ConfigurationTitle variant="h4">
                Audit History
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Show Audit History"
                    propertyPath={`${configurationPropertyPath}.showAudit`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
            </ConfigurationBox>
            
            <ConfigurationTitle variant="h4">
                Discrepancy Notes
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Show Discrepancy Notes"
                    propertyPath={`${configurationPropertyPath}.showNotes`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
                <Condition 
                    mode={context.usingTrialConfiguration ? ConditionMode.Enable : ConditionMode.Show}
                    subscription={{ value: true }}
                    condition={showNoteCondition}
                    onConditionNotMet={onConditionNotMet}
                >
                    <InputOnlyField 
                        label="Types"
                        propertyPath={`${configurationPropertyPath}.noteTypes`}
                        component={Multiselect}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    >
                        <MenuItem
                            value={NoteType.ValidationFailed}
                        >
                            Validation Failed
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Annotation}
                        >
                            Annotation
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Query}
                        >
                            Query
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ReasonForChange}
                        >
                            Reason For Change
                        </MenuItem>
                        <MenuItem
                            value={NoteType.Internal}
                        >
                            Internal
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ProtocolDeviation}
                        >
                            Protocol Deviation
                        </MenuItem>
                        <MenuItem
                            value={NoteType.ImportantProtocolDeviation}
                        >
                            Important Protocol Deviation
                        </MenuItem>
                    </InputOnlyField>
                    <InputOnlyField 
                        label="Statuses"
                        propertyPath={`${configurationPropertyPath}.noteStatuses`}
                        component={Multiselect}
                        FormControlProps={{
                            margin: 'normal'
                        }}
                    >
                        <MenuItem
                            value={NoteStatus.New}
                        >
                            New
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.Updated}
                        >
                            Updated
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.ResolutionProposed}
                        >
                            Resolution Proposed
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.Closed}
                        >
                            Closed
                        </MenuItem>
                        <MenuItem
                            value={NoteStatus.NotApplicable}
                        >
                            Not Applicable
                        </MenuItem>
                    </InputOnlyField>
                </Condition>
            </ConfigurationBox>
        </ConfigurationFormEditPanel>
    )
}

export default ConfigurationFormEditField;